import { type ApiFetchContext, apiFetch } from "../client";
import type { BuildHeadersInput } from "../headers";
import { computeSignture, nowTimestamp } from "../sign";
import type {
  EditMyInfoRequest,
  LoginRequest,
  LoginVo,
  RegisterRequest,
  SendEmailCaptchaRequest,
  UpdateLoginPasswordRequest,
  UserInfo,
  UserStatistics,
} from "../types/user";
import type { ScoreLogRecord } from "../types/wallet";

export async function getUserInfo(
  ctx: ApiFetchContext,
  authToken: string,
  opts: Pick<BuildHeadersInput, "currency"> = {},
): Promise<UserInfo> {
  return apiFetch<UserInfo>(ctx, "/user/info", { authToken, ...opts });
}

/** `GET /user/statistics` (StatisticsVo) — per-user dashboard counts. Auth required. */
export async function getUserStatistics(
  ctx: ApiFetchContext,
  authToken: string,
): Promise<UserStatistics> {
  return apiFetch<UserStatistics>(ctx, "/user/statistics", { authToken });
}

/** POST /user/email/captcha — sends a 6-digit code. Backend keys it by (type, email). */
export async function sendEmailCaptcha(
  ctx: ApiFetchContext,
  body: SendEmailCaptchaRequest,
): Promise<string> {
  return apiFetch<string>(ctx, "/user/email/captcha", { method: "POST", body });
}

/** POST /user/register — needs signture + timestamp headers per `UserController.java:1207`. */
export async function registerUser(ctx: ApiFetchContext, body: RegisterRequest): Promise<LoginVo> {
  const timestamp = nowTimestamp();
  const signture = computeSignture(body.username, body.password, timestamp);
  return apiFetch<LoginVo>(ctx, "/user/register", {
    method: "POST",
    body,
    signture,
    timestamp,
  });
}

/** POST /user/login — requires a fresh Yandex SmartCaptcha token in captchaToken. */
export async function loginUser(ctx: ApiFetchContext, body: LoginRequest): Promise<LoginVo> {
  return apiFetch<LoginVo>(ctx, "/user/login", {
    method: "POST",
    body,
  });
}

/**
 * POST /user/login/password/update — change the login password.
 * Backend (`UserController.updateLoginPassword`) reads `UpdateLoginPasswordParam`
 * { oldPassword, newPassword } and validates the old password against the stored
 * hash. Requires a valid JWT (auth token).
 */
export async function updateLoginPassword(
  ctx: ApiFetchContext,
  authToken: string,
  body: UpdateLoginPasswordRequest,
): Promise<string> {
  return apiFetch<string>(ctx, "/user/login/password/update", {
    method: "POST",
    body,
    authToken,
  });
}

/**
 * GET /user/wallet/score/log — paginated score (points) change history.
 * Backend: `UserController` / `ScoreLogVo[]` (aps-boot#472).
 * Returns raw array directly (not wrapped in a page object).
 * `pageNo` starts at 1 · `limit` capped at 100 by the backend.
 */
export async function getScoreLog(
  ctx: ApiFetchContext,
  authToken: string,
  input: { pageNo?: number; limit?: number } = {},
): Promise<ScoreLogRecord[]> {
  const parts: string[] = [];
  if (input.pageNo !== undefined) parts.push(`pageNo=${input.pageNo}`);
  if (input.limit !== undefined) parts.push(`limit=${input.limit}`);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  return apiFetch<ScoreLogRecord[]>(ctx, `/user/wallet/score/log${qs}`, { authToken });
}

/**
 * POST /user/info/edit — update editable profile fields.
 * Backend `UserController.editorMyInfo` reads `EditorMyInfoParam`
 * (nikeName / avatar / lang / currencyCode / mobile / …). Requires a valid JWT.
 * We only send the fields the caller provides.
 */
export async function editMyInfo(
  ctx: ApiFetchContext,
  authToken: string,
  body: EditMyInfoRequest,
): Promise<string> {
  return apiFetch<string>(ctx, "/user/info/edit", {
    method: "POST",
    body,
    authToken,
  });
}
